import _debounce from 'lodash/debounce'
import { Logger, type ILogger, Presenter, type IPresenterOwner } from 'cube'

import { DominioSelectScope, type DominioOption } from './analise-bulas.scope'
import queryDominios, { type BulaDominioRequest, type BulaDominioResponse, type BulaDominioIten } from './service/query-dominios'

type DominioName = keyof typeof queryDominios
type DominioService = (rq: BulaDominioRequest) => Promise<BulaDominioResponse>

export default class DominioSelectPresenter extends Presenter<DominioSelectScope> {

    readonly log: ILogger
    readonly service: DominioService
    private selectedCodeArray = new Array<string>()
    private selectedOptionMap = new Map<string, DominioOption>()

    public onCheckChangedListener?: (selectedCodes: string[]) => void

    constructor(owner: IPresenterOwner, scope: DominioSelectScope, dominio: DominioName, dominioQuery: (rq: BulaDominioRequest) => Promise<BulaDominioResponse>) {
        super(owner, scope)
        this.log = Logger.get(`vgm-selector-${dominio}`)
        this.service = dominioQuery

        this.scope.onSearchChange = _debounce(this.action(this.onSearchChange), 500)
        this.scope.onChangeOptions = this.action(this.onChangeOptions)
    }

    get selectedCodes() {
        return this.selectedCodeArray
    }

    async setSelectedExistingCodes(codes: string[]) {
        Utils.retainMap(this.selectedOptionMap, codes)
        await this.onChangeOptions([...this.selectedOptionMap.values()])
    }

    async setSelectedCodes(codes: string[]) {
        // Abaixo o valor "loading = false" eh proposital. O motio eh que
        // o componente visual possui um bug que abaca abrindo o lookup
        this.scope.loading = false
        try {
            let codeSet = new Set<string>(codes)
            for (const curSelCode of [...this.selectedOptionMap.keys()]) {
                if (codeSet.has(curSelCode)) {
                    codeSet.delete(curSelCode)
                } else {
                    this.selectedOptionMap.delete(curSelCode)
                }
            }

            let response: BulaDominioResponse = { duration: 0, itens: [] }
            if (codeSet.size > 0) {
                // Neste caso tem algo a ser buscado no servidor
                response = await this.service({ codes: [...codeSet] })
            } else {
                // Neste caso, já temos os dados, mas vamos só ficar com o que foi
                // solicitado
                codeSet = new Set<string>(codes)
                for (const option of this.scope.options) {
                    if (codeSet.has(option.value)) {
                        response.itens.push({
                            id: option.value,
                            descricao: option.label
                        })
                    }
                }
            }

            this._updateAfterFetch(response)
            await this.onChangeOptions(this.scope.options)
        } finally {
            this.scope.loading = false
        }
    }

    async forceContent(options: BulaDominioIten[]) {
        Utils.retainMap(this.selectedOptionMap, options.map(o => o.id))
        this._updateAfterFetch({ duration: 0, itens: options })
        await this.onChangeOptions(this.scope.options)
    }

    async onLimpar() {
        this.selectedOptionMap.clear()
        this.scope.value = []
    }

    async onSearchChange(text: string) {
        this.log.debug(`onSearchChange: ${text}`)

        this.scope.loading = true
        try {
            const response = await this.service({ text, limit: 100 })
            this._updateAfterFetch(response)
        } finally {
            this.scope.loading = false
        }
    }

    protected _updateAfterFetch(response: BulaDominioResponse) {
        const newOptionMap = new Map<string, DominioOption>()

        for (const item of response.itens) {
            newOptionMap.set(item.id, { label: item.descricao, value: item.id })
        }

        for (const item of this.selectedOptionMap.values()) {
            newOptionMap.set(item.value, item)
        }

        const options: DominioOption[] = [...newOptionMap.values()]
        const localeOptions: Intl.CollatorOptions = { sensitivity: 'base' }
        options.sort((a, b) => a.label.localeCompare(b.label, undefined, localeOptions))
        this.scope.options = options
    }

    async onChangeOptions(selectedOptions: DominioOption[]) {
        this.selectedOptionMap.clear()
        this.selectedCodeArray.length = 0
        for (const option of selectedOptions) {
            this.selectedOptionMap.set(option.value, option)
            this.selectedCodeArray.push(option.value)
        }
        this.scope.value = this.selectedCodeArray
        this.onCheckChangedListener && this.onCheckChangedListener(this.selectedCodeArray)
    }

}

const Utils = {

    retainMap<K>(map: Map<K, unknown>, keys: K[]) {
        const tmpMap = new Set<unknown>(keys)
        for (const curKey of [...map.keys()]) {
            if (!tmpMap.has(curKey)) {
                map.delete(curKey)
            }
        }
    }

}